import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionContext } from '../../context/SessionContext';
import { AnalysisFilterOptions, getAnalysisFilters } from '../../services/analysisService';

/** The filters every analysis page shares, kept in the URL. */
export type ScopeKey = 'session' | 'class' | 'student' | 'exam' | 'subject';

/** Changing a filter clears the choices made under it. */
const DEPENDENTS: Record<ScopeKey, ScopeKey[]> = {
  session: ['class', 'student', 'exam'],
  class: ['student', 'exam', 'subject'],
  student: [],
  exam: [],
  subject: [],
};

/**
 * The last choices, so moving between Student Progress, Subject Performance
 * and Class Performance keeps the same session, class and exam.
 */
const remembered: Partial<Record<ScopeKey, string>> = {};

/** `wanted` when the list has it, else the fallback when the list has that, else the first item. */
export const pickId = (wanted: string, list: Array<{ _id: string }>, fallback?: string | null): string => {
  if (wanted && list.some((i) => i._id === wanted)) return wanted;
  if (fallback && list.some((i) => i._id === fallback)) return fallback;
  return list[0]?._id ?? '';
};

export interface AnalysisScope {
  options: AnalysisFilterOptions;
  sessionId: string;
  classId: string;
  /** The chosen class as a reader would say it: "Class 9" for a bare "9". */
  className: string;
  sessionName: string;
  /** The center of the chosen session; analysis calls are scoped by it. */
  centerId: string;
  sessions: AnalysisFilterOptions['sessions'];
  classes: AnalysisFilterOptions['classes'];
  /** Published exams of the chosen session and class, newest first. */
  exams: AnalysisFilterOptions['exams'];
  multiCenter: boolean;
  activeSessionId?: string;
  /** The raw choice for a filter: from the URL, else remembered, else ''. */
  raw: (key: ScopeKey) => string;
  set: (key: ScopeKey, value: string) => void;
  /** Several filters at once, e.g. jumping from a class chart to one student. */
  setMany: (values: Partial<Record<ScopeKey, string>>) => void;
}

/**
 * Loads the analysis filter options once and resolves the session and class
 * to sensible defaults (the active session, its busiest class), so each page
 * shows results on arrival.
 */
export const useAnalysisScope = () => {
  const { activeSession } = useSessionContext();
  const [params, setParams] = useSearchParams();
  const [options, setOptions] = useState<AnalysisFilterOptions | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAnalysisFilters()
      .then((o) => {
        if (cancelled) return;
        // A school with no centers/exams omits those keys; normalise once here.
        setOptions({
          ...o,
          centers: o?.centers ?? [],
          sessions: o?.sessions ?? [],
          classes: o?.classes ?? [],
          subjects: o?.subjects ?? [],
          students: o?.students ?? [],
          exams: o?.exams ?? [],
        });
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || 'Could not load the analysis filters');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    params.forEach((value, key) => {
      if (key in DEPENDENTS) remembered[key as ScopeKey] = value;
    });
  }, [params]);

  const raw = useCallback((key: ScopeKey) => params.get(key) ?? remembered[key] ?? '', [params]);

  const setMany = useCallback((values: Partial<Record<ScopeKey, string>>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      (Object.keys(values) as ScopeKey[]).forEach((key) => {
        [key, ...DEPENDENTS[key]].forEach((k) => {
          if (k in values && k !== key) return;
          next.delete(k);
          delete remembered[k];
        });
      });
      (Object.entries(values) as Array<[ScopeKey, string]>).forEach(([key, value]) => {
        if (!value) return;
        next.set(key, value);
        remembered[key] = value;
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const set = useCallback((key: ScopeKey, value: string) => setMany({ [key]: value }), [setMany]);

  const scope = useMemo<AnalysisScope | null>(() => {
    if (!options) return null;
    const sessions = options.sessions;
    const activeId = activeSession?._id ?? sessions.find((s) => s.status === 'active')?._id;
    const sessionId = pickId(raw('session'), sessions, activeId);
    const session = sessions.find((s) => s._id === sessionId);
    const centerId = session?.centerId?._id ?? '';

    const classes = options.classes.filter((c) => !centerId || c.centerId?._id === centerId);
    const classId = pickId(raw('class'), classes, session?.activeClassId);

    const rawClassName = classes.find((c) => c._id === classId)?.name ?? '';
    const className = /^\d/.test(rawClassName) ? `Class ${rawClassName}` : rawClassName;

    const exams = options.exams.filter(
      (e) => e.sessionId === sessionId && (e.classIds ?? []).includes(classId),
    );

    return {
      options, sessionId, classId, centerId, className, sessionName: session?.name ?? '', sessions, classes, exams,
      multiCenter: options.centers.length > 1,
      activeSessionId: activeId,
      raw, set, setMany,
    };
  }, [options, activeSession?._id, raw, set, setMany]);

  return { scope, error };
};
